/**
 * Simple Favorites Service - Just GraphQL API calls
 */

import { generateClient } from 'aws-amplify/api';
import { getCurrentUser } from 'aws-amplify/auth';
import { 
  createUserFavorite, 
  deleteUserFavorite 
} from '../graphql/mutations';

const client = generateClient();

// Simple query to get favorites with product details
const getUserFavoritesWithProducts = /* GraphQL */ `
  query UserFavoritesByUserId(
    $userId: ID!
    $limit: Int
    $nextToken: String
  ) {
    userFavoritesByUserId(
      userId: $userId
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        userId
        productId
        createdAt
        product {
          id
          name
          description
          price
          discountPrice
          stock
          isActive
          images {
            items {
              id
              url
              altText
              isPrimary
            }
          }
        }
      }
      nextToken
    }
  }
`;

class FavoritesService {
  
  async addToFavorites(productId) {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const userId = user.username || user.userId;
      
      const result = await client.graphql({
        query: createUserFavorite,
        variables: {
          input: {
            userId: userId,
            productId: productId
          }
        }
      });
      
      return result.data.createUserFavorite;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  }

  async removeFromFavorites(productId) {
    try {
      const user = await getCurrentUser();
      if (!user) throw new Error('User must be logged in');

      const userId = user.username || user.userId;
      
      // First find the favorite to delete
      const favorites = await this.getUserFavorites({ limit: 1000 });
      const favoriteToDelete = favorites.favorites.find(f => f.productId === productId);
      
      if (!favoriteToDelete) {
        console.log('Favorite not found');
        return false;
      }

      await client.graphql({
        query: deleteUserFavorite,
        variables: {
          input: { id: favoriteToDelete.id }
        }
      });
      
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error;
    }
  }

  async toggleFavorite(productId) {
    try {
      const isFavorite = await this.isFavorite(productId);
      
      if (isFavorite) {
        await this.removeFromFavorites(productId);
        return { action: 'removed', isFavorite: false };
      } else {
        const favorite = await this.addToFavorites(productId);
        return { action: 'added', isFavorite: true, favorite };
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  }

  async isFavorite(productId) {
    try {
      const user = await getCurrentUser();
      if (!user) return false;

      const favorites = await this.getUserFavorites({ limit: 1000 });
      return favorites.favorites.some(f => f.productId === productId);
    } catch (error) {
      console.error('Error checking favorite:', error);
      return false;
    }
  }

  async getUserFavorites(options = {}) {
    try {
      const user = await getCurrentUser();
      if (!user) {
        return { favorites: [], nextToken: null, totalCount: 0 };
      }

      const userId = user.username || user.userId;
      const { limit = 20, nextToken = null } = options;

      const result = await client.graphql({
        query: getUserFavoritesWithProducts,
        variables: {
          userId: userId,
          limit: limit,
          nextToken: nextToken
        }
      });

      const favorites = result.data.userFavoritesByUserId.items || [];
      
      return {
        favorites: favorites,
        nextToken: result.data.userFavoritesByUserId.nextToken,
        totalCount: favorites.length
      };
    } catch (error) {
      console.error('Error fetching favorites:', error);
      return { favorites: [], nextToken: null, totalCount: 0 };
    }
  }

  async getFavoritesCount() {
    try {
      const favorites = await this.getUserFavorites({ limit: 1000 });
      return favorites.totalCount;
    } catch (error) {
      console.error('Error getting favorites count:', error);
      return 0;
    }
  }
}

export default new FavoritesService();